import Phaser from "phaser";
import { ArtPipeline, type AnimState } from "../../art";
import { loadDropfrontArtPack } from "../../data/art/dropfront-pack";
import { loadFrontlineArtPack } from "../../data/art/frontline-pack";
import { loadPortalFfaArtPack } from "../../data/art/portal-ffa-pack";
import { loadRaidArtPack } from "../../data/art/raid-pack";
import { loadInfiniteWarArtPack } from "../../data/art/infinite-war-pack";
import { dropfrontCatalog } from "../../data/dropfront-catalog";
import { frontlineCatalog } from "../../data/frontline-catalog";
import { portalFfaCatalog } from "../../data/portal-ffa-catalog";
import { raidCatalog } from "../../data/raid-catalog";
import { infiniteWarCatalog } from "../../data/infinite-war-catalog";
import {
  hitPresetId,
  listVfxPresetIds,
  playTimedAttackFx,
  resolveAttackFx,
  resolveAttackOrigin,
  VfxPlayer,
} from "../../vfx";
import {
  dropfrontGripPack,
  frontlineGripPack,
  portalFfaGripPack,
  raidGripPack,
  infiniteWarGripPack,
  resolveRigConfig,
  UnitRig,
  type GripPack,
  type RigTables,
} from "../../view/unit-rig";
import type { AttackFxTables } from "../../vfx/attack-fx";
import {
  formatCharacterQcReport,
  qcBadge,
  qcColor,
  runCharacterQc,
  type CharacterQcReport,
} from "./character-qc";
import {
  compositeBitsComplete,
  DF_COMBAT,
  DF_PROPS,
  spritePackForCharacter,
  type ArtLabPackId,
  type ArtLabState,
} from "./art-lab-state";
import {
  measurePropQuality,
  measureStructureQuality,
  measureTileQuality,
  resolveSpriteKind,
} from "./kind-quality";

const WALK_ENTER = 22;
const BODY_SIZE = 72;
const DEFAULT_SWING_SEC = 0.32;

function rigTablesFor(packId: ArtLabPackId): RigTables {
  if (packId === "dropfront") return dropfrontCatalog.view.rig as RigTables;
  if (packId === "frontline") return frontlineCatalog.view.rig as RigTables;
  if (packId === "raid") return raidCatalog.view.rig as unknown as RigTables;
  if (packId === "infinite-war") {
    return infiniteWarCatalog.view.rig as unknown as RigTables;
  }
  return portalFfaCatalog.rig as RigTables;
}

function attackFxFor(packId: ArtLabPackId): AttackFxTables | undefined {
  if (packId === "dropfront") {
    return dropfrontCatalog.view.attackFx as AttackFxTables;
  }
  if (packId === "frontline") {
    return frontlineCatalog.view.attackFx as AttackFxTables;
  }
  if (packId === "raid") {
    return raidCatalog.view.attackFx as AttackFxTables;
  }
  if (packId === "infinite-war") {
    return infiniteWarCatalog.view.attackFx as unknown as AttackFxTables;
  }
  return portalFfaCatalog.attackFx as AttackFxTables | undefined;
}

function gripFor(packId: ArtLabPackId, characterId: string): GripPack | null {
  if (packId === "portal-ffa") {
    return characterId === "weapon" ? null : portalFfaGripPack;
  }
  if (packId === "frontline") {
    return characterId.startsWith("fl_")
      ? frontlineGripPack
      : dropfrontGripPack;
  }
  if (packId === "raid") return raidGripPack;
  if (packId === "infinite-war") return infiniteWarGripPack;
  return dropfrontGripPack;
}

function loadPackForContinuity(packId: string) {
  if (packId === "frontline") return loadFrontlineArtPack();
  if (packId === "portal-ffa") return loadPortalFfaArtPack();
  if (packId === "raid") return loadRaidArtPack();
  if (packId === "infinite-war") return loadInfiniteWarArtPack();
  return loadDropfrontArtPack();
}

/**
 * Phaser stage — one Character instance (body + optional UnitRig weapon).
 */
export class ArtLabStage {
  private readonly scene: Phaser.Scene;
  private readonly vfx: VfxPlayer;
  private root!: Phaser.GameObjects.Container;
  private body!: Phaser.GameObjects.Image;
  private rig: UnitRig | null = null;
  private victim!: Phaser.GameObjects.Image;
  private victimRoot!: Phaser.GameObjects.Container;
  private label!: Phaser.GameObjects.Text;
  private contractText!: Phaser.GameObjects.Text;
  private trailBudget = { left: 8 };
  private lastTexKey = "";
  private rigKey = "";
  private attackPulse = 0;
  private qcKey = "";
  private qcReport: CharacterQcReport | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.vfx = new VfxPlayer(scene);
  }

  create(): void {
    const { width, height } = this.scene.scale;
    const cx = width * 0.5;
    const cy = height * 0.48;

    this.root = this.scene.add.container(cx, cy).setDepth(10);
    const key = ArtPipeline.ensureSprite(
      this.scene,
      "dropfront",
      "assault",
      "ally",
    );
    this.body = this.scene.add
      .image(0, 0, key)
      .setDisplaySize(BODY_SIZE, BODY_SIZE);
    this.root.add(this.body);

    this.victimRoot = this.scene.add.container(cx + 90, cy).setDepth(9);
    const vKey = ArtPipeline.ensureSprite(
      this.scene,
      "dropfront",
      "swarm",
      "enemy",
    );
    this.victim = this.scene.add
      .image(0, 0, vKey)
      .setDisplaySize(48, 48);
    this.victimRoot.add(this.victim);

    this.label = this.scene.add
      .text(cx, cy + 70, "", {
        fontFamily: "monospace",
        fontSize: "12px",
        color: "#9aa3c7",
        align: "center",
      })
      .setOrigin(0.5)
      .setDepth(12);

    this.contractText = this.scene.add
      .text(cx, height * 0.78, "", {
        fontFamily: "monospace",
        fontSize: "11px",
        color: "#7dffb0",
        align: "center",
        wordWrap: { width: width * 0.9 },
      })
      .setOrigin(0.5, 0)
      .setDepth(12)
      .setVisible(false);
  }

  destroy(): void {
    // Kill hit/knock tweens so onComplete cannot touch destroyed targets mid-quit.
    if (this.body) this.scene.tweens.killTweensOf(this.body);
    if (this.victim) this.scene.tweens.killTweensOf(this.victim);
    if (this.victimRoot) this.scene.tweens.killTweensOf(this.victimRoot);
    if (this.rig) this.scene.tweens.killTweensOf(this.rig.container);

    this.disposeRig({ reparentBody: false });
    this.body?.destroy(true);
    this.root?.destroy(true);
    this.victimRoot?.destroy(true);
    this.label?.destroy();
    this.contractText?.destroy();
    this.body = undefined!;
    this.root = undefined!;
    this.victimRoot = undefined!;
  }

  triggerAttack(): void {
    this.attackPulse = 0.28;
  }

  /**
   * Timed attack VFX from muzzle (combat parity). Also drives attack pulse.
   * Callers should mark Composite affiliation bits after play.
   */
  playVfxBurst(state: ArtLabState): void {
    this.triggerAttack();
    state.anim = "attack";
    state.animTime = 0;

    const angle = (state.vfxAngleDeg * Math.PI) / 180;
    const id = state.characterId;
    const fx = resolveAttackFx(attackFxFor(state.packId), id);
    const tables = rigTablesFor(state.packId);
    const cfg = resolveRigConfig(tables, id);
    const swingSec = Number(cfg.swingSec) || DEFAULT_SWING_SEC;

    this.ensureRig(state);
    const muzzle = this.rig
      ? this.rig.muzzleWorld({ x: this.root.x, y: this.root.y })
      : null;
    const { x: ox, y: oy } = resolveAttackOrigin(
      this.root.x,
      this.root.y,
      muzzle,
    );

    const vx = this.victimRoot.x;
    const vy = this.victimRoot.y;

    // Portal-ffa may remap hit via attackStyle when hitPreset empty.
    let hitOverride = fx;
    if (state.packId === "portal-ffa") {
      const hit =
        fx.hitPreset || hitPresetId(cfg.attackStyle, "single");
      hitOverride = { ...fx, hitPreset: hit };
    }

    playTimedAttackFx({
      scene: this.scene,
      vfx: this.vfx,
      fx: hitOverride,
      affiliation: state.affiliation,
      packId: fx.spriteFxPackId ?? state.packId,
      originX: ox,
      originY: oy,
      impactX: vx,
      impactY: vy,
      angle,
      swingSec,
      cellPx: BODY_SIZE,
      depth: 8,
      floatDepth: 12,
      hitTargets: {
        victim: this.victimRoot,
        victimBody: this.victim,
        victimRig: this.victim,
      },
      damage: 12,
      targetKind: "enemy",
    });
  }

  sync(state: ArtLabState, dt: number): null {
    if (!this.body?.active || !this.root?.active) return null;

    // Body = clip only; Rig/VFX/Composite = full character (body+weapon).
    const showRig =
      state.panel === "rig" ||
      state.panel === "vfx" ||
      state.panel === "composite";
    const showContract = state.panel === "contract";
    const showVictim =
      state.panel === "vfx" || state.panel === "composite";
    this.contractText.setVisible(showContract);
    this.victimRoot.setVisible(showVictim);

    if (
      (state.panel === "body" || state.panel === "composite") &&
      state.anim === "attack" &&
      state.animTime > 0.04
    ) {
      state.seenBodyAttack = true;
    }

    if (state.autoPlay) {
      state.animTime += dt;
      if (state.anim === "attack") {
        const pack = spritePackForCharacter(state.packId, state.characterId);
        const dur = ArtPipeline.animClipDuration(
          pack,
          state.characterId,
          state.anim,
          state.facing,
        );
        if (dur > 0 && Number.isFinite(dur) && state.animTime >= dur) {
          state.animTime %= dur;
        }
      }
    }

    const anim = (this.attackPulse > 0 ? "attack" : state.anim) as AnimState;
    if (this.attackPulse > 0) {
      this.attackPulse = Math.max(0, this.attackPulse - dt);
    }

    const spritePack = spritePackForCharacter(state.packId, state.characterId);
    const { key } = ArtPipeline.ensureAnimFrame(
      this.scene,
      spritePack,
      state.characterId,
      state.affiliation,
      anim,
      state.animTime,
      state.facing,
    );
    if (key !== this.lastTexKey) {
      this.body.setTexture(key);
      this.body.setDisplaySize(BODY_SIZE, BODY_SIZE);
      this.lastTexKey = key;
    }

    const aim = (state.aimDeg * Math.PI) / 180;
    const facingSign: 1 | -1 = Math.cos(aim) >= 0 ? 1 : -1;

    if (showRig) {
      this.ensureRig(state);
      if (this.rig) {
        this.trailBudget.left = 8;
        this.rig.update(
          {
            aimAngle: aim,
            animState: anim,
            animTime: state.animTime,
            intentVx:
              state.anim === "walk" && state.bobOn ? Math.cos(aim) * 40 : 0,
            intentVy:
              state.anim === "walk" && state.bobOn ? Math.sin(aim) * 40 : 0,
            facing: facingSign,
            dying: false,
            lockBodyFlip: true,
            facingDir: state.facing,
          },
          this.trailBudget,
          this.scene,
          { x: this.root.x, y: this.root.y },
        );
      }
    } else {
      this.clearRig();
      if (this.body.parentContainer !== this.root) {
        this.root.add(this.body);
      }
      this.body.setPosition(0, 0);
      this.body.setVisible(true);
      this.body.setFlipX(false);
    }

    const tables = rigTablesFor(state.packId);
    const kit = resolveRigConfig(tables, state.characterId);
    const qc = this.ensureCharacterQc(state);
    const bitsOk = compositeBitsComplete(state);
    const badge = qc ? ` ${qcBadge(qc.overall)}` : "";
    const bitTag = bitsOk ? "" : " [COMPOSITE INCOMPLETE]";
    this.label.setText(
      `${state.packId}/${state.characterId} · ${kit.weaponShape}/${kit.attackStyle} · ${state.facing} · ${anim}${badge}${bitTag}`,
    );

    if (showContract) {
      const bitLine = bitsOk
        ? "Composite bits: OK (body + ally + enemy)"
        : `Composite bits INCOMPLETE — body=${state.seenBodyAttack ? "✓" : "✗"} ally=${state.seenCompositeAlly ? "✓" : "✗"} enemy=${state.seenCompositeEnemy ? "✓" : "✗"}`;
      if (qc && !qc.items.some((i) => i.id === "dirs" && i.severity === "fail")) {
        const stamp = bitsOk ? qc.overall : "warn";
        this.contractText.setColor(qcColor(stamp === "warn" ? "warn" : qc.overall));
        this.contractText.setText(
          [
            formatCharacterQcReport(qc),
            bitLine,
            bitsOk
              ? `Vitest: runCharacterQc · ${state.packId}`
              : "Stamp: INCOMPLETE until Composite Play (ally+enemy) + body attack scrub",
          ].join("\n"),
        );
        return null;
      }
      // Props / tiles / side sprites — kind-scoped contract
      if (state.packId === "dropfront") {
        const pack = loadDropfrontArtPack();
        const rows = pack.sprites[state.characterId]?.rows;
        const kind = resolveSpriteKind(
          state.characterId,
          pack.manifest.spriteKinds as
            | Record<string, "character" | "structure" | "tile" | "prop" | "vfx">
            | undefined,
        );
        if (rows && kind !== "character") {
          const q =
            kind === "structure"
              ? measureStructureQuality(rows)
              : kind === "tile"
                ? measureTileQuality(rows)
                : measurePropQuality(rows);
          this.contractText.setColor(q.ok ? "#7dffb0" : "#ff8a80");
          this.contractText.setText(
            [
              `${state.characterId} kind=${kind}`,
              `bbox ${q.bboxWidth}x${q.bboxHeight} solid ${q.solidCount} keys ${q.uniqueOpaqueKeys} ${q.ok ? "OK" : "FAIL"}`,
              ...q.failReasons.map((r) => `  · ${r}`),
              `combat=${(DF_COMBAT as readonly string[]).includes(state.characterId)} propList=${(DF_PROPS as readonly string[]).includes(state.characterId)}`,
            ].join("\n"),
          );
          return null;
        }
      }
      if (qc) {
        this.contractText.setColor(qcColor(qc.overall));
        this.contractText.setText(
          [formatCharacterQcReport(qc), bitLine].join("\n"),
        );
        return null;
      }
      this.contractText.setColor("#9aa3c7");
      this.contractText.setText(
        `No authored dirs for ${state.packId}/${state.characterId}.\n${bitLine}`,
      );
    }
    return null;
  }

  private ensureCharacterQc(state: ArtLabState): CharacterQcReport | null {
    const artPackId = spritePackForCharacter(state.packId, state.characterId);
    const key = `${state.packId}:${artPackId}:${state.characterId}`;
    if (this.qcKey === key && this.qcReport) return this.qcReport;

    const pack = loadPackForContinuity(artPackId);
    const dirs = pack.anims[state.characterId]?.dirs;
    if (!dirs?.e?.idle) {
      this.qcKey = key;
      this.qcReport = null;
      return null;
    }

    this.qcReport = runCharacterQc({
      packId: artPackId,
      characterId: state.characterId,
      pack,
      grips: gripFor(state.packId, state.characterId),
      rig: rigTablesFor(state.packId),
      attackFx: attackFxFor(state.packId),
      presetIds: listVfxPresetIds(),
      humanoidProportion: artPackId === "dropfront",
    });
    this.qcKey = key;
    return this.qcReport;
  }

  private ensureRig(state: ArtLabState): void {
    const nextKey = `${state.packId}:${state.characterId}`;
    if (this.rig && this.rigKey === nextKey) return;

    this.clearRig();
    const tables = rigTablesFor(state.packId);
    const grip = gripFor(state.packId, state.characterId);
    this.body.setPosition(0, 0);
    this.rig = new UnitRig(
      this.scene,
      this.body,
      state.characterId,
      tables,
      WALK_ENTER,
      undefined,
      grip,
    );
    this.root.add(this.rig.container);
    this.rigKey = nextKey;
  }

  private clearRig(): void {
    this.disposeRig({ reparentBody: true });
  }

  /** Detach UnitRig; optionally put body back under root (live panel switch). */
  private disposeRig(opts: { reparentBody: boolean }): void {
    if (!this.rig) return;
    const body = this.body;
    if (body?.parentContainer) {
      body.parentContainer.remove(body, false);
    }
    try {
      this.rig.destroy();
    } finally {
      this.rig = null;
      this.rigKey = "";
    }
    if (
      opts.reparentBody &&
      body?.active &&
      this.root?.active &&
      body.parentContainer !== this.root
    ) {
      this.root.add(body);
    }
  }
}
